import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projects } from '../data/projects';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

const ProjectDetails = () => {
  const { id } = useParams();
  const { isDarkMode } = useTheme();
  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <div className={`min-h-screen py-32 ${isDarkMode ? 'bg-black' : 'bg-stone-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className={`font-serif text-4xl font-normal mb-8 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>Project not found</h1>
          <Link to="/projects" className={`font-serif ${isDarkMode ? 'text-red-500' : 'text-stone-800'} hover:underline`}>
            ← Back to projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-32 ${isDarkMode ? 'bg-black' : 'bg-stone-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/projects" className={`font-serif ${isDarkMode ? 'text-red-500' : 'text-stone-800'} hover:underline mb-8 block`}>
            ← Back to projects
          </Link>
          <div className="max-w-3xl">
            <h1 className={`font-serif text-4xl font-normal mb-6 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>{project.title}</h1>
            <p className={`font-serif text-lg ${isDarkMode ? 'text-gray-300' : 'text-stone-500'} mb-8`}>{project.description}</p>
              <div className="flex gap-6 mb-10">
                {project.githubLink && (
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-serif flex items-center gap-2 ${isDarkMode ? 'text-white hover:text-red-500' : 'text-stone-900 hover:text-stone-700'}`}
                  >
                    <FaGithub />
                    <span>View Source</span>
                  </a>
                )}
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`font-serif flex items-center gap-2 ${isDarkMode ? 'text-white hover:text-red-500' : 'text-stone-900 hover:text-stone-700'}`}
                >
                  <FaExternalLinkAlt />
                  <span>Visit Project</span>
                </a>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-10">
                {project.metrics.map((metric) => (
                  <div key={metric.label} className="text-center">
                    <div className={`font-serif text-2xl font-normal mb-1 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>{metric.value}</div>
                    <div className={`font-serif text-sm ${isDarkMode ? 'text-gray-400' : 'text-stone-500'}`}>
                      {metric.label}
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <h2 className={`font-serif text-2xl font-normal mb-6 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>Technologies Used</h2>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className={`font-serif px-3 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-stone-100 text-stone-800'} rounded-full text-sm`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetails;